import {ReactNode} from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {METRIC_GRANULARITIES, MetricComparisonPeriod, MetricGranularity} from "@/lib/types/metricsExplorer.ts";
import {EXPLORER_CHART_TYPES, ExplorerChartType} from "@/pages/metricsExplorer/explorerConfig.ts";

import styles from "./MetricsExplorerToolbar.module.scss";

interface MetricsExplorerToolbarProps {
  start: Date;
  end: Date;
  fixedRange?: StandardDateRange;
  granularity: MetricGranularity;
  comparison: MetricComparisonPeriod | null;
  chartType: ExplorerChartType;
  onGranularityChange: (granularity: MetricGranularity) => void;
  onComparisonChange: (comparison: MetricComparisonPeriod | null) => void;
  onChartTypeChange: (chartType: ExplorerChartType) => void;
  onRangeChange: (start: Date | null, end: Date | null, fixedRange: StandardDateRange | null) => void;
  /** Actions that belong on the toolbar row, after the chart-type picker. */
  children?: ReactNode;
}

const GRANULARITY_LABELS: Record<MetricGranularity, string> = {
  AUTO: "Auto",
  ONE_MINUTE: "1 minute",
  FIFTEEN_MINUTES: "15 minutes",
  ONE_HOUR: "1 hour",
  ONE_DAY: "1 day",
  ONE_WEEK: "1 week",
  ONE_MONTH: "1 month",
};

const COMPARISON_LABELS: Record<MetricComparisonPeriod, string> = {
  PREVIOUS_PERIOD: "Previous period",
  PREVIOUS_DAY: "Previous day",
  PREVIOUS_WEEK: "Previous week",
  PREVIOUS_MONTH: "Previous month",
  PREVIOUS_YEAR: "Previous year",
  CUSTOM_OFFSET: "Custom offset",
};

const CHART_TYPE_LABELS: Record<ExplorerChartType, string> = {
  line: "Line",
  area: "Area",
  stackedArea: "Stacked area",
  column: "Bars",
  stackedColumn: "Stacked bars",
  scatter: "Scatter (correlation)",
  distribution: "Distribution",
  heatmap: "Seasonality heatmap",
};

const NO_COMPARISON = "NONE";

// CUSTOM_OFFSET needs an offset field the toolbar does not have yet, so it is not offered.
const OFFERED_COMPARISONS: MetricComparisonPeriod[] = [
  "PREVIOUS_PERIOD",
  "PREVIOUS_DAY",
  "PREVIOUS_WEEK",
  "PREVIOUS_MONTH",
  "PREVIOUS_YEAR",
];

const toOption = (value: string, label: string): SelectOption => ({value, label});

export const MetricsExplorerToolbar = ({
  start,
  end,
  fixedRange,
  granularity,
  comparison,
  chartType,
  onGranularityChange,
  onComparisonChange,
  onChartTypeChange,
  onRangeChange,
  children,
}: MetricsExplorerToolbarProps) => {
  const {user} = useCurrentUserContext();

  return (
    <div className={styles.toolbar}>
      <div>
        <FormLabel forId="explorer-time-range">Time range</FormLabel>
        <DatePicker
          mode="range"
          selected={{from: start, to: end}}
          fixedRange={fixedRange}
          timezone={user!.account!.timezone}
          dateFormat={user!.current_date_format}
          onChange={(date, selectedFixedRange) =>
            onRangeChange(date?.from ?? null, date?.to ?? null, selectedFixedRange ?? null)
          }
          disableFutureDates
          hasPopoverInput
          inputProps={{size: "small", id: "explorer-time-range"}}
        />
      </div>

      <div className={styles.control}>
        <FormLabel forId="explorer-granularity">Interval</FormLabel>
        <Select
          inputId="explorer-granularity"
          size="small"
          options={METRIC_GRANULARITIES.map((value) => toOption(value, GRANULARITY_LABELS[value]))}
          value={toOption(granularity, GRANULARITY_LABELS[granularity])}
          onChange={(option) =>
            onGranularityChange(((option as SelectOption | null)?.value ?? "AUTO") as MetricGranularity)
          }
        />
      </div>

      <div className={styles.control}>
        <FormLabel forId="explorer-comparison">Compare to</FormLabel>
        <Select
          inputId="explorer-comparison"
          size="small"
          options={[
            toOption(NO_COMPARISON, "No comparison"),
            ...OFFERED_COMPARISONS.map((value) => toOption(value, COMPARISON_LABELS[value])),
          ]}
          value={
            comparison ? toOption(comparison, COMPARISON_LABELS[comparison]) : toOption(NO_COMPARISON, "No comparison")
          }
          onChange={(option) => {
            const value = (option as SelectOption | null)?.value ?? NO_COMPARISON;
            onComparisonChange(value === NO_COMPARISON ? null : (value as MetricComparisonPeriod));
          }}
        />
      </div>

      <div className={styles.control}>
        <FormLabel forId="explorer-chart-type">Chart</FormLabel>
        <Select
          inputId="explorer-chart-type"
          size="small"
          options={EXPLORER_CHART_TYPES.map((value) => toOption(value, CHART_TYPE_LABELS[value]))}
          value={toOption(chartType, CHART_TYPE_LABELS[chartType])}
          onChange={(option) =>
            onChartTypeChange(((option as SelectOption | null)?.value ?? "line") as ExplorerChartType)
          }
        />
      </div>

      {children && <div className={styles.actions}>{children}</div>}
    </div>
  );
};
